import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// ── Styles ────────────────────────────────────────────────────────────────────
// Local palette — mirrors the pattern used by other standalone dialogs in this
// app (e.g. DeleteMemberDialog.jsx): each dialog component owns its own tokens
// rather than importing the host page's module-scoped constants.
const C = {
  navy:   '#072146',
  dark:   '#1e293b',
  muted:  '#64748b',
  border: '#e2e8f0',
  green:  '#00C853',
};

const btnBase = {
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 700,
  fontSize: 13,
};

const cancelBtn = {
  ...btnBase,
  fontWeight: 600,
  borderColor: C.border,
  color: C.navy,
};

const confirmBtn = {
  ...btnBase,
  bgcolor: C.green,
  color: '#1a1a1a',
  '&:hover': { bgcolor: '#00b048' },
  '&.Mui-disabled': { bgcolor: 'rgba(0,200,83,0.35)', color: 'rgba(0,0,0,0.5)' },
};

function InfoBox({ tone, icon, testId, children }) {
  const palette = tone === 'warning'
    ? { bg: '#fffbeb', border: '#fde68a', color: '#92400e', iconColor: '#b45309' }
    : { bg: '#eff6ff', border: '#bfdbfe', color: '#1e3a8a', iconColor: '#1d4ed8' };
  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'flex', gap: 1.25, alignItems: 'flex-start',
        bgcolor: palette.bg, border: `1px solid ${palette.border}`,
        borderRadius: '10px', p: 1.5,
      }}
    >
      {React.cloneElement(icon, { sx: { color: palette.iconColor, fontSize: 19, flexShrink: 0, mt: 0.1 } })}
      <Typography sx={{ fontSize: 13, color: palette.color, lineHeight: 1.6 }}>
        {children}
      </Typography>
    </Box>
  );
}

/**
 * Warns an admin, before saving, what changing the effective currency on an
 * Entity or a Space will do to existing items and advertising campaigns —
 * both are converted at a live rate, or relabeled only (no rate available).
 * Items additionally have a multi-currency skip case (already priced in more
 * than one currency, needs manual review) — campaigns have no equivalent,
 * since each campaign only ever has one set of money fields. Deliberately
 * doesn't surface exact counts in the copy — impact.itemCount/campaignCount/
 * itemsSkippedMultiCurrency are still used to decide WHICH message applies,
 * just not printed verbatim.
 *
 * Props:
 *   open              bool
 *   onClose           () => void                 — Cancel
 *   onConfirm         () => Promise<void>|void   — proceed with the actual save
 *   loading           bool   — impact preview is still being fetched
 *   impact            {
 *     itemCount, itemsSkippedMultiCurrency, campaignCount, willConvert, rate,
 *     rateDate, oldCurrencyIso, newCurrencyIso, affectedSpaceCount
 *   } | null
 *   oldCurrencyIso    string — best-effort label resolved by the caller from
 *                              currencyOptions, used while `impact` is still
 *                              loading (impact.oldCurrencyIso wins once known)
 *   newCurrencyIso    string — same, for the new currency
 */
export default function CurrencyChangeConfirmDialog({
  open,
  onClose,
  onConfirm,
  loading,
  impact,
  oldCurrencyIso,
  newCurrencyIso,
}) {
  const [confirming, setConfirming] = useState(false);

  // Reset the local "saving" flag whenever the dialog is reopened for a new change.
  useEffect(() => {
    if (open) setConfirming(false);
  }, [open]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  };

  // `||` not `??` — the backend's CurrencyImpactDto defaults OldCurrencyIso/NewCurrencyIso to
  // "" (never null/undefined) when a currency can't be resolved, e.g. a brand-new entity with
  // no currency set yet. "" ?? x never falls through since "" isn't null/undefined, so the
  // caller-supplied fallback (and '—') would silently never trigger with ??.
  const resolvedOldIso = impact?.oldCurrencyIso || oldCurrencyIso || '—';
  const resolvedNewIso = impact?.newCurrencyIso || newCurrencyIso || '—';

  const noItemsAffected = !loading && impact
    && impact.itemCount === 0 && (impact.itemsSkippedMultiCurrency ?? 0) === 0
    && (impact.campaignCount ?? 0) === 0;
  const anyConvertibleContent = !!impact && (impact.itemCount > 0 || (impact.campaignCount ?? 0) > 0);
  const spaceScope = impact?.affectedSpaceCount > 1
    ? `across ${impact.affectedSpaceCount} spaces`
    : 'in this space';

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: C.navy, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CurrencyExchangeOutlinedIcon sx={{ fontSize: 20, color: C.navy }} />
        Confirm Currency Change
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 3 }}>
            <CircularProgress size={26} sx={{ color: C.navy }} />
            <Typography sx={{ fontSize: 13, color: C.muted }}>
              Checking how many items this affects…
            </Typography>
          </Box>
        ) : !impact ? (
          <Typography sx={{ fontSize: 13, color: C.muted }}>
            Could not load the impact of this change. Please close and try again.
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: 13, color: C.dark, lineHeight: 1.6 }}>
              Changing the currency from <strong>{resolvedOldIso}</strong> to <strong>{resolvedNewIso}</strong> will
              update the currency existing items resolve against.
            </Typography>

            {anyConvertibleContent && impact.willConvert && (
              <InfoBox tone="info" icon={<InfoOutlinedIcon />} testId="currency-impact-convert">
                All items and advertisement costs {spaceScope} will update based on today's
                exchange rate: 1 {resolvedOldIso} = {impact.rate} {resolvedNewIso}{' '}
                (published {impact.rateDate}).
              </InfoBox>
            )}

            {anyConvertibleContent && !impact.willConvert && (
              <InfoBox tone="info" icon={<InfoOutlinedIcon />} testId="currency-impact-relabel">
                No live exchange rate is available for this pair — items and advertisement costs will
                be relabeled only, with the same stored amount under the new currency code.
              </InfoBox>
            )}

            {impact.itemsSkippedMultiCurrency > 0 && (
              <InfoBox tone="warning" icon={<WarningAmberRoundedIcon />} testId="currency-impact-skip">
                Some items already have prices in more than one currency and will be skipped —
                review them manually.
              </InfoBox>
            )}

            {noItemsAffected && (
              <Typography data-testid="currency-impact-none" sx={{ fontSize: 13, color: C.muted }}>
                No existing items or advertisement costs will be affected.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={confirming} sx={cancelBtn}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || !impact || confirming}
          startIcon={confirming ? <CircularProgress size={14} sx={{ color: 'rgba(0,0,0,0.6)' }} /> : null}
          sx={confirmBtn}
        >
          {confirming ? 'Updating…' : 'Update Currency'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
